import { useCallback } from 'react';
import React from "react";
import { toast } from "react-toastify";
import { DndProvider } from "react-dnd";
import { useLocation, useNavigate, unstable_useBlocker } from "react-router-dom";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import Toolbar from "./components/Toolbar";
import VideoPreview from "./components/VideoPreview";
import Timeline from "./components/Timeline";
import TextInputModal from "./components/TextInputModal";
import CaptionsCustomizationModal from "./components/CaptionsCustomizationModal";
import { useTimeline } from "./hooks/useTimeline";
import { Play, Pause, SkipBack, SkipForward, ChevronDown, ZoomIn, ZoomOut, Minimize2, MonitorPlay } from "lucide-react";
import { formatTime, generateId } from "./utils/dragDropTypes";
import RightSidebar from "./components/RightSidebar";
import { downloadRemoteMedia } from "./utils/fetchRemoteMedia";
import { getDesignCanvas, getDocumentAspectRatio } from "./utils/reframe";
import { createExportPayload, createSnapshotFromDocument } from "./utils/exportSnapshot";
import LeftSidebar from "./components/LeftSidebar";
import MusicLibraryModal from "./components/MusicLibraryModal";
import AdAssetLibraryModal from "./components/AdAssetLibraryModal";
import SoundEffectModal from "./components/SoundEffectModal";
// VariationsBar removed — variations now handled by RightSidebar
import ConfirmModal from "../Modals/ConfirmModal";
import services from "../../API";
import ResponsivePanel from "../Layout/ResponsivePanel";

// Timeline data will be fetched from API based on videoAdId

const EXPORT_JOB_STORAGE_PREFIX = "zimzee-video-editor-export:";

const getExportJobStorageKey = (videoAdId) => `${EXPORT_JOB_STORAGE_PREFIX}${videoAdId}`;

const readStoredExportJob = (videoAdId) => {
    if (!videoAdId) return null;

    try {
        const rawJob = localStorage.getItem(getExportJobStorageKey(videoAdId));
        const job = rawJob ? JSON.parse(rawJob) : null;
        return job?.jobId ? job : null;
    } catch {
        return null;
    }
};

const storeExportJob = (videoAdId, job) => {
    if (!videoAdId || !job?.jobId) return;

    try {
        localStorage.setItem(getExportJobStorageKey(videoAdId), JSON.stringify(job));
    } catch {
        // Exporting must still work when browser storage is unavailable.
    }
};

const removeStoredExportJob = (videoAdId) => {
    if (!videoAdId) return;

    try {
        localStorage.removeItem(getExportJobStorageKey(videoAdId));
    } catch {
        // Nothing else to do when browser storage is unavailable.
    }
};

const getVideoAdAudioUrl = (videoAd) => {
    if (!videoAd || typeof videoAd !== "object") return null;

    return (
        videoAd.audioUrl ||
        videoAd.audio?.url ||
        videoAd.audioFile?.url ||
        (typeof videoAd.audioFile === "string" ? videoAd.audioFile : null) ||
        videoAd.voiceoverUrl ||
        videoAd.voiceover?.url ||
        null
    );
};

const getTimelineVoiceAudioUrl = (document) => {
    const items = document?.timeline?.items;
    if (!Array.isArray(items)) return null;

    const voiceCategories = new Set(["voice", "speech", "narration", "dialogue"]);
    const voiceAudioItem = items.find((item) => {
        const type = item?.type?.toString().toLowerCase();
        const category = item?.category?.toString().toLowerCase();
        return type === "audio" && voiceCategories.has(category) && item?.file?.url;
    });

    return voiceAudioItem?.file?.url || null;
};

function VideoEditorWorkspace() {
    const location = useLocation();
    const navigate = useNavigate();
    const videoAdId = location.state?.videoAdId;

    const handleBack = () => {
        if (location.state?.returnUrl) {
            navigate(location.state.returnUrl, { state: { ...location.state } });
        } else {
            navigate(-1);
        }
    };

    const {
        layers,
        setLayers,
        currentTime,
        duration,
        isPlaying,
        zoom,
        aspectRatio,
        setZoom,
        setAspectRatio,
        addLayer,
        removeLayer,
        toggleLayerVisibility,
        toggleLayerLock,
        toggleLayerMute,
        addClipToLayer,
        removeClip,
        play,
        pause,
        stop,
        seek,
        updateClip,
        updateLayerTransform,
        selectedClipId,
        selectClip: baseSelectClip,
        reorderLayers,
        splitClip,
        updateLayerVolume,
        toggleSharedTransform,
    } = useTimeline();

    // Find selected clip and layer
    let selectedClip = null;
    let selectedLayerId = null;
    if (selectedClipId && layers) {
        layers.forEach((l) => {
            const c = l.clips.find((c) => c.id === selectedClipId);
            if (c) {
                selectedClip = c;
                selectedLayerId = l.id;
            }
        });
    }

    // Export State
    const [exportRatios, setExportRatios] = React.useState(["9:16"]);
    const [isExporting, setIsExporting] = React.useState(false);
    const [exportProgress, setExportProgress] = React.useState(0);
    const exportPollingCancelledRef = React.useRef(false);
    const exportInFlightRef = React.useRef(false);

    // Preview Overlay State
    const [previewRatio, setPreviewRatio] = React.useState("9:16");
    const [originalAspectRatio, setOriginalAspectRatio] = React.useState("9:16");

    // Export Crop State
    const [exportCropOffsets, setExportCropOffsets] = React.useState({});
    const [activeCropFormat, setActiveCropFormat] = React.useState(null);

    // Other Modals
    const [isTextModalOpen, setIsTextModalOpen] = React.useState(false);
    const [isMusicModalOpen, setIsMusicModalOpen] = React.useState(false);
    const [isAssetModalOpen, setIsAssetModalOpen] = React.useState(false);
    const [assetLibraryCategory, setAssetLibraryCategory] = React.useState('creativeAssets');
    const [isCaptionsModalOpen, setIsCaptionsModalOpen] = React.useState(false);
    const [isCaptionSelected, setIsCaptionSelected] = React.useState(false);
    const [isSoundEffectModalOpen, setIsSoundEffectModalOpen] = React.useState(false);
    const [dynamicCaptions, setDynamicCaptions] = React.useState(null);
    const [isGeneratingCaptions, setIsGeneratingCaptions] = React.useState(false);
    const [selectedTextLayerId, setSelectedTextLayerId] = React.useState(null);

    // Loading state
    const [isLoading, setIsLoading] = React.useState(true);
    const [loadingMessage, setLoadingMessage] = React.useState("Initializing...");
    const [loadingProgress, setLoadingProgress] = React.useState(0);

    // Unsaved Changes State
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = React.useState(false);
    // `undefined` means no switch is pending; `null` is a valid target for Original.
    const [pendingVariationId, setPendingVariationId] = React.useState(undefined);
    const [pendingNewVariationFormat, setPendingNewVariationFormat] = React.useState(null);

    // Initial load ref to prevent dirty flag on first render
    const isInitialLoad = React.useRef(true);

    // Wrap mutators to track changes
    const handleMutate = (fn) => (...args) => {
        fn(...args);
        if (!isLoading && !isInitialLoad.current) {
            setHasUnsavedChanges(true);
        }
    };

    const handleUpdateExportCropOffsets = (nextOffsets) => {
        setExportCropOffsets(nextOffsets);
        if (!isLoading && !isInitialLoad.current) {
            setHasUnsavedChanges(true);
        }
    };

    const handleUpdateDynamicCaptions = (nextCaptions) => {
        setDynamicCaptions(nextCaptions);
        if (!isLoading && !isInitialLoad.current) {
            setHasUnsavedChanges(true);
        }
    };

    const wrappedAddLayer = handleMutate(addLayer);
    const wrappedRemoveLayer = handleMutate(removeLayer);
    const wrappedToggleLayerVisibility = handleMutate(toggleLayerVisibility);
    const wrappedToggleLayerLock = handleMutate(toggleLayerLock);
    const wrappedToggleLayerMute = handleMutate(toggleLayerMute);
    const wrappedAddClipToLayer = handleMutate(addClipToLayer);
    const wrappedRemoveClip = handleMutate(removeClip);
    const wrappedUpdateClip = handleMutate(updateClip);
    const wrappedUpdateLayerTransform = handleMutate(updateLayerTransform);
    const wrappedReorderLayers = handleMutate(reorderLayers);
    const wrappedSplitClip = handleMutate(splitClip);
    const wrappedUpdateLayerVolume = handleMutate(updateLayerVolume);
    const wrappedToggleSharedTransform = handleMutate(toggleSharedTransform);


    // Handle spacebar play/pause
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            // Ignore if user is typing in an input, textarea, or contenteditable
            const isInputEvent =
                e.target.tagName === 'INPUT' ||
                e.target.tagName === 'TEXTAREA' ||
                e.target.isContentEditable;

            if (e.code === 'Space' && !isInputEvent) {
                e.preventDefault(); // Prevent scrolling down the page
                if (isPlaying) {
                    pause();
                } else {
                    play();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isPlaying, play, pause]);

    // Warn on page leave (browser refresh/close)
    React.useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    // Intercept internal navigation (back button / link clicks)
    const blocker = unstable_useBlocker(
        ({ nextLocation }) =>
            hasUnsavedChanges && location.pathname !== nextLocation?.pathname
    );

    // Open modal when navigation is blocked
    React.useEffect(() => {
        if (blocker.state === "blocked") {
            setIsUnsavedChangesModalOpen(true);
        }
    }, [blocker.state]);

    // Reset initial load ref after loading finishes
    React.useEffect(() => {
        if (!isLoading) {
            // Small timeout to allow state to settle
            const timer = setTimeout(() => {
                isInitialLoad.current = false;
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);
    const [isSaving, setIsSaving] = React.useState(false);
    const [isTimelineCollapsed, setIsTimelineCollapsed] = React.useState(false);
    const [isLeftPanelOpen, setIsLeftPanelOpen] = React.useState(false);
    const [isRightPanelOpen, setIsRightPanelOpen] = React.useState(false);

    // Variations state
    const [variations, setVariations] = React.useState([]);
    const [activeVariationId, setActiveVariationId] = React.useState(null);
    const [isSavingVariation, setIsSavingVariation] = React.useState(false);
    const [, setIsAnimatingCollapse] = React.useState(false);
    const [selectedForExport, setSelectedForExport] = React.useState(new Set());

    // Video Ad Meta — stores adType + regeneration payload from API
    const [videoAdMeta, setVideoAdMeta] = React.useState(null);

    // Text Editing State
    const [editingClip, setEditingClip] = React.useState(null);
    const [deletingVariationId, setDeletingVariationId] = React.useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
    const [variationToDelete, setVariationToDelete] = React.useState(null);

    // Draggable section state
    const [bottomSectionHeight, setBottomSectionHeight] = React.useState(45); // percentage - adjusted upward from 40
    const [isDraggingDivider, setIsDraggingDivider] = React.useState(false);
    const containerRef = React.useRef(null);

    // Track if media has been loaded to prevent duplicate loading
    const hasLoadedMedia = React.useRef(false);

    const onAddMedia = () => {
        setIsAssetModalOpen(true);
    };

    const handleRegenerateCaptions = async () => {
        if (!videoAdId) {
            alert("No Video Ad ID found. Cannot regenerate captions.");
            return;
        }

        try {
            setIsGeneratingCaptions(true);
            const response = await services.generateCaptions(videoAdId);
            const captionsData = response.data?.data?.captions || response.data?.captions;

            if (captionsData) {
                const rawApp = captionsData.appearance || { x: 0.5, y: 0.7, scale: 1, maxWidth: 0.8 };
                const normApp = {
                    x: 0.5,
                    y: rawApp.y > 1 ? (rawApp.y - 20) / 1280 : (rawApp.y || 0.7),
                    scale: rawApp.scale || 1,
                    maxWidth: rawApp.maxWidth || 0.8
                };

                if (normApp.y < 0.2) normApp.y = 0.5;
                if (normApp.y > 0.9) normApp.y = 0.8;

                setDynamicCaptions({
                    content: Array.isArray(captionsData) ? captionsData : (captionsData.content || []),
                    appearance: normApp,
                    styles: captionsData.style || captionsData.styles || {}
                });

                setLayers(prev => {
                    const filtered = prev.filter(layer =>
                        layer.name !== "Captions" &&
                        !layer.name.toLowerCase().includes("subtitle") &&
                        layer.id !== "captions-layer"
                    );

                    const content = Array.isArray(captionsData) ? captionsData : (captionsData.content || []);
                    if (content.length > 0) {
                        const captionsLayer = {
                            id: 'captions-layer',
                            name: 'Captions',
                            type: 'captions',
                            visible: true,
                            locked: false,
                            muted: false,
                            zIndex: 999,
                            clips: content.map((segment, idx) => ({
                                id: `caption-clip-${idx}`,
                                startTime: segment.startTime || 0,
                                duration: segment.duration || 1,
                                text: segment.text || (segment.words || []).map(w => w.content).join(' '),
                                words: segment.words || [],
                            })),
                        };
                        return [captionsLayer, ...filtered];
                    }
                    return filtered;
                });
            }
        } catch (error) {
            console.error("Error regenerating captions:", error);
            alert("Failed to regenerate captions.");
        } finally {
            setIsGeneratingCaptions(false);
        }
    };

    const reloadLayersFromDocument = useCallback(async (docRoot, forcedAspectRatio = null) => {
        try {
            setIsLoading(true);
            setLoadingProgress(0);

            setAspectRatio(forcedAspectRatio || getDocumentAspectRatio(docRoot));

            if (docRoot.captions) {
                const cData = docRoot.captions;
                const normApp = cData.appearance ? {
                    x: cData.appearance.x !== undefined ? cData.appearance.x : 0.5,
                    y: cData.appearance.y !== undefined ? cData.appearance.y : 0.8,
                    scale: cData.appearance.scale !== undefined ? cData.appearance.scale : 1,
                    maxWidth: cData.appearance.maxWidth !== undefined ? cData.appearance.maxWidth : 0.8
                } : { x: 0.5, y: 0.8, scale: 1, maxWidth: 0.8 };

                setDynamicCaptions({
                    content: Array.isArray(cData.content) ? cData.content : [],
                    appearance: normApp,
                    styles: cData.styles || cData.style || {},
                    settings: cData.settings || {},
                });
            } else {
                setDynamicCaptions(null);
            }

            const items = docRoot.timeline?.items || [];
            const groupedItems = items.reduce((acc, item) => {
                const id = item.id || generateId();
                if (!acc[id]) acc[id] = [];
                acc[id].push(item);
                return acc;
            }, {});

            const newLayers = [];
            const mediaCache = new Map();
            const groupIds = Object.keys(groupedItems);

            for (let i = 0; i < groupIds.length; i++) {
                const layerId = groupIds[i];
                const layerItems = groupedItems[layerId];
                const layerClips = [];
                let layerProperties = null;

                for (const item of layerItems) {
                    if (!layerProperties) {
                        layerProperties = {
                            id: layerId,
                            name: item.category ? (item.category.charAt(0).toUpperCase() + item.category.slice(1)) : layerId,
                            type: item.type.toLowerCase(),
                            zIndex: item.zIndex === undefined ? 0 : item.zIndex,
                            volume: item.volume !== undefined ? item.volume : 1, // Restore volume
                            muted: item.muted || false, // Restore muted
                            category: item.category || (item.type === "VIDEO" ? "main" : "voice"),
                            sharedTransform: item.sharedTransform,
                        };
                    }

                    const fileUrl = item.file?.url;
                    if (!fileUrl && item.type !== "TEXT") continue;

                    let mediaFile = null;
                    if (item.type !== "TEXT") {
                        if (mediaCache.has(fileUrl)) {
                            const cachedMedia = mediaCache.get(fileUrl);
                            mediaFile = { ...cachedMedia, id: generateId() };
                        } else {
                            setLoadingMessage(`Downloading ${item.category || item.type.toLowerCase()}...`);
                            let duration = 0;
                            let url = fileUrl;

                            try {
                                const file = await downloadRemoteMedia(fileUrl, item.file.name || "asset", item.file.type);
                                url = URL.createObjectURL(file);
                            } catch (err) {
                                console.warn("Failed to download remote media as blob, falling back to direct URL:", fileUrl, err);
                                // Fallback to using the direct URL if fetch fails (e.g., due to CORS)
                                url = fileUrl;
                            }

                            // Try to extract duration if it's video or audio
                            if (item.type === "VIDEO") {
                                const videoEl = document.createElement("video");
                                videoEl.src = url;
                                await new Promise((resolve) => {
                                    videoEl.onloadedmetadata = () => { duration = videoEl.duration; resolve(); };
                                    videoEl.onerror = resolve;
                                });
                            } else if (item.type === "AUDIO") {
                                const audioEl = document.createElement("audio");
                                audioEl.src = url;
                                await new Promise((resolve) => {
                                    audioEl.onloadedmetadata = () => { duration = audioEl.duration; resolve(); };
                                    audioEl.onerror = resolve;
                                });
                            }

                            mediaFile = {
                                id: generateId(),
                                name: item.file.name || item.id,
                                type: item.file.type || (item.type === "AUDIO" ? "audio/mpeg" : "video/mp4"),
                                url: url,
                                duration: duration,
                                sourceType: "remote",
                                sourceUrl: fileUrl,
                            };
                            mediaCache.set(fileUrl, mediaFile);
                        }
                    } else {
                        mediaFile = { id: generateId(), type: "text", name: "Text" };
                    }

                    const clip = {
                        id: generateId(),
                        mediaFile: mediaFile,
                        startTime: item.trim?.start !== undefined ? item.trim.start : (item.startTime || 0),
                        duration: item.trim?.duration !== undefined ? item.trim.duration : (mediaFile?.duration || 5),
                        trimStart: item.trim?.offset !== undefined ? item.trim.offset : (item.trim?.start || 0),
                        trimEnd: (item.trim?.offset || 0) + (item.trim?.duration || 5),
                        transform: item.appearance ? { ...item.appearance } : { x: 0.5, y: 0.5, scale: 1 },
                        text: item.text,
                        style: item.style,
                        animation: item.animation,
                        visible: item.visible !== undefined ? item.visible : true,
                        muted: item.clipMuted || false,
                    };
                    layerClips.push(clip);
                }

                if (layerProperties && layerClips.length > 0) {
                    newLayers.push({
                        ...layerProperties,
                        clips: layerClips,
                        visible: layerItems[0].layerVisible ?? layerItems[0].visible ?? true,
                        muted: layerItems[0].muted !== undefined ? layerItems[0].muted : false,
                    });
                }

                // Update progress AFTER the layer group is fully processed
                const progress = (i + 1) / groupIds.length;
                setLoadingProgress(progress);
            }

            if (docRoot.captions) {
                const captionsContent = Array.isArray(docRoot.captions.content) ? docRoot.captions.content : [];
                if (captionsContent.length > 0) {
                    const captionsLayer = {
                        id: 'captions-layer',
                        name: 'Captions',
                        type: 'captions',
                        visible: docRoot.captions.visible !== false,
                        clips: captionsContent.map((segment, idx) => ({
                            id: `caption-clip-${idx}`,
                            startTime: segment.startTime || 0,
                            duration: segment.duration || 1,
                            text: segment.text || (segment.words || []).map(w => w.content).join(' '),
                            words: segment.words || [],
                        })),
                    };
                    newLayers.unshift(captionsLayer);
                }
            }

            newLayers.sort((a, b) => a.zIndex - b.zIndex);
            setLayers(newLayers);
            setLoadingMessage("Ready!");
        } catch (error) {
            console.error("Error reloading layers:", error);
            setLoadingMessage("Error loading project.");
        } finally {
            setTimeout(() => setIsLoading(false), 500);
        }
    }, [setLayers, setAspectRatio]);

    React.useEffect(() => {
        if (!videoAdId || hasLoadedMedia.current) return;
        hasLoadedMedia.current = true;

        const loadTimelineData = async () => {
            try {
                setIsLoading(true);
                setLoadingProgress(0);
                setLoadingMessage("Fetching timeline...");

                const response = await services.getEditorTimeline(videoAdId);
                const editorTimeline = response?.data?.editorTimeline || response?.editorTimeline || response?.data;

                if (!editorTimeline || typeof editorTimeline !== 'object') {
                    throw new Error("No timeline data found in response");
                }

                const docRoot = editorTimeline.document || editorTimeline;
                await reloadLayersFromDocument(docRoot);
                const originalRatio = getDocumentAspectRatio(docRoot);
                setOriginalAspectRatio(originalRatio);
                setPreviewRatio(originalRatio);
                setExportRatios([originalRatio]);

                const variationsData = editorTimeline.variations || response?.data?.variations || [];
                setVariations(variationsData);
                setActiveVariationId(null);

                // Capture meta for regeneration (adType, character, script, brand, voice, background media)
                // Try multiple response shapes — the meta can live in different places depending on the API version
                const metaVideoAd = editorTimeline.meta?.videoAd || editorTimeline.videoAd || null;
                const docAdType = docRoot?.adType || editorTimeline?.adType || metaVideoAd?.adType || null;

                // Only cache meta when the response actually contains meaningful fields.
                // If the timeline API returns no character/voice/script data, we leave videoAdMeta null
                // so the fallback getVideoAdById fetch runs in handleRegenerateScene.
                // NOTE: characterId may be a populated object { _id, name } — extract _id first.
                const extractedCharacterId = metaVideoAd?.characterId?._id || metaVideoAd?.characterId || metaVideoAd?.character?._id || metaVideoAd?.character || null;
                const extractedVoiceId = metaVideoAd?.voiceId || metaVideoAd?.voice?._id || metaVideoAd?.voice?.voiceId || metaVideoAd?.voice || null;
                const extractedScript = metaVideoAd?.scriptText || metaVideoAd?.script || null;
                const extractedAudioUrl = getVideoAdAudioUrl(metaVideoAd) || getTimelineVoiceAudioUrl(docRoot);

                if (docAdType || extractedCharacterId || extractedVoiceId || extractedScript) {
                    setVideoAdMeta({
                        adType: docAdType || null,
                        characterId: extractedCharacterId,
                        gender:
                            metaVideoAd?.characterId?.attributes?.gender ||
                            metaVideoAd?.character?.attributes?.gender ||
                            metaVideoAd?.characterId?.gender ||
                            metaVideoAd?.character?.gender ||
                            metaVideoAd?.gender ||
                            null,
                        scriptText: extractedScript,
                        voiceId: extractedVoiceId,
                        brandId: metaVideoAd?.brandId?._id || metaVideoAd?.brandId || metaVideoAd?.brand?._id || metaVideoAd?.brand || null,
                        voiceSettings: metaVideoAd?.voiceSettings || null,
                        // Some timeline API versions omit videoAd.audioUrl even though the
                        // generated speech is present as the timeline's voice audio track.
                        audioUrl: extractedAudioUrl,
                        backgroundMedia: metaVideoAd?.backgroundMedia || null,
                    });
                }
            } catch (error) {
                console.error("Error loading timeline data:", error);
                setLoadingMessage("Error loading project.");
                setIsLoading(false);
            }
        };

        loadTimelineData();
    }, [videoAdId, reloadLayersFromDocument]);

    const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.25));
    const handleToggleTimeline = () => {
        setIsAnimatingCollapse(true);
        setIsTimelineCollapsed(!isTimelineCollapsed);
        setTimeout(() => setIsAnimatingCollapse(false), 400);
    };

    const timelineContainerRef = React.useRef(null);
    const handleFitToScreen = () => {
        if (timelineContainerRef.current && duration > 0) {
            const availableWidth = Math.max(0, timelineContainerRef.current.clientWidth - 50);
            const newZoom = availableWidth / (duration * 50);
            setZoom(Math.max(0.1, Math.min(newZoom, 3)));
        }
    };

    const videoPreviewRef = React.useRef(null);
    const audioInputRef = React.useRef(null);

    const processUploadedFile = async (e, type) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setIsLoading(true);
            const url = URL.createObjectURL(file);
            let duration = 0;
            if (type === "audio") {
                const audioEl = document.createElement("audio");
                audioEl.src = url;
                await new Promise(r => audioEl.onloadedmetadata = r);
                duration = audioEl.duration;
            }
            const newLayerId = generateId();
            const newClipId = generateId();
            const mediaFile = { id: generateId(), name: file.name, type: file.type, url, duration, sourceType: "local" };
            const newClip = { id: newClipId, mediaFile, startTime: currentTime, duration, trimStart: 0, trimEnd: duration };
            const newLayer = { id: newLayerId, name: `${type}_layer`, type, clips: [newClip], visible: true, muted: false, category: "voice" };
            setLayers((prev) => [...prev, newLayer]);
        } finally { setIsLoading(false); }
    };

    const handleGlobalAddText = () => {
        setSelectedTextLayerId(null);
        setEditingClip(null);
        setIsTextModalOpen(true);
    };

    const toggleExportRatio = (ratio) => {
        setExportRatios([ratio]);
        setPreviewRatio(ratio);
        if (activeCropFormat !== ratio) setActiveCropFormat(null);
    };

    const handleToggleExportSelection = (id) => {
        setSelectedForExport(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const triggerExportDownload = React.useCallback(async (outputUrl, exportJob) => {
        const fileName = `${(exportJob.title || 'Video').replace(/[:\\/*?"<>|]/g, '-')}-${(exportJob.variationRatio || '9:16').replace(":", "-")}.${exportJob.format || 'mp4'}`;

        try {
            const response = await fetch(outputUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = fileName;
            anchor.click();
            window.URL.revokeObjectURL(url);
        } catch {
            // Fall back to the export URL when CORS prevents fetching the file as a blob.
            window.open(outputUrl, '_blank');
        }
    }, []);

    const pollExportJob = React.useCallback(async (exportJob) => {
        exportPollingCancelledRef.current = false;

        while (!exportPollingCancelledRef.current) {
            try {
                const statusRes = await services.getExportStatus(videoAdId, exportJob.jobId);
                if (exportPollingCancelledRef.current) return { cancelled: true };

                const job = statusRes.data || statusRes;
                if (job.status === 'Completed') {
                    setExportProgress(100);
                    setLoadingMessage('Preparing your download...');
                    await triggerExportDownload(job.outputUrl, exportJob);
                    removeStoredExportJob(videoAdId);
                    toast.success(exportJob.format === 'jpg' || exportJob.format === 'png' ? "Your image export is ready" : "Your video export is ready");
                    return { completed: true };
                }

                if (job.status === 'Failed') {
                    removeStoredExportJob(videoAdId);
                    const exportFailure = new Error(job.error || "Export failed on the server.");
                    exportFailure.isExportFailure = true;
                    throw exportFailure;
                }

                const progress = job.progress || 0;
                setExportProgress(progress);
                setLoadingMessage(`Exporting... ${progress}%`);
                storeExportJob(videoAdId, { ...exportJob, progress });
            } catch (error) {
                if (exportPollingCancelledRef.current) return { cancelled: true };

                // A transient network failure should not discard a server-side job.
                // Keep the persisted job and retry while the editor remains open.
                if (error?.isExportFailure) throw error;
                setLoadingMessage('Export is still processing. Retrying...');
            }

            await new Promise((resolve) => setTimeout(resolve, 10000));
        }

        return { cancelled: true };
    }, [triggerExportDownload, videoAdId]);

    // The server continues exporting after the editor unmounts. Rehydrate its job
    // and resume polling whenever the user returns to this video editor.
    React.useEffect(() => {
        let isCurrentEditor = true;
        exportPollingCancelledRef.current = false;
        const savedJob = readStoredExportJob(videoAdId);

        if (savedJob) {
            setIsExporting(true);
            setExportProgress(savedJob.progress || 0);
            setLoadingMessage(`Exporting... ${savedJob.progress || 0}%`);

            pollExportJob(savedJob)
                .catch((error) => {
                    if (isCurrentEditor) {
                        console.error("Resumed export error:", error);
                        alert(`Export failed: ${error.message || "Unknown error"}`);
                    }
                })
                .finally(() => {
                    if (isCurrentEditor) {
                        setIsExporting(false);
                        setExportProgress(0);
                    }
                });
        }

        return () => {
            isCurrentEditor = false;
            exportPollingCancelledRef.current = true;
        };
    }, [pollExportJob, videoAdId]);

    const executeExport = async (exportId, settings = { format: 'mp4', quality: 'high' }) => {
        if (isExporting || exportInFlightRef.current) {
            toast.info("An export is already in progress.");
            return;
        }
        const isActive = exportId === (activeVariationId || 'original');
        if (isActive && isLoading) {
            toast.info("Please wait for the project to finish loading.");
            return;
        }

        try {
            // Capture the displayed canvas and all authored values BEFORE any
            // export/progress state update. Export never switches the UI format.
            const activePayload = isActive ? createExportPayload({
                layers, captions: dynamicCaptions, duration, aspectRatio,
                format: previewRatio || aspectRatio, cropOffsets: exportCropOffsets,
                frame: videoPreviewRef.current?.getExportFrame?.(),
            }, settings) : null;

            exportInFlightRef.current = true;
            exportPollingCancelledRef.current = false;
            setIsExporting(true);
            setExportProgress(0);
            setLoadingMessage("Preparing export...");

            const variation = variations.find(v => (v.id || v._id) === exportId);
            const title = exportId === 'original' ? 'Original' : variation?.title || 'Variation';
            let payload = activePayload;
            if (!payload) {
                let document;
                let progress = {};
                if (exportId === 'original') {
                    const response = await services.getEditorTimeline(videoAdId);
                    const timeline = response?.data?.editorTimeline || response?.editorTimeline || response?.data;
                    document = timeline?.document || timeline;
                } else {
                    if (!variation) throw new Error("The requested variation was not found.");
                    progress = variation.editorProgress || {};
                    document = progress.document || progress;
                }
                // Inactive downloads read their saved document independently.
                // Awaiting a React state setter cannot update this async closure.
                const snapshot = await createSnapshotFromDocument(document, progress);
                payload = createExportPayload(snapshot, settings);
            }

            setLoadingMessage("Starting export...");
            let jobId;
            try {
                const res = await services.exportVideo(videoAdId, payload);
                jobId = res.jobId || res.data?.jobId;
            } catch (error) {
                if (error.response?.status === 429) {
                    toast.info("An export is already in progress. Please wait for it to finish.");
                    return;
                }
                throw error;
            }
            if (!jobId) throw new Error("The export service did not return a job ID.");

            const exportJob = {
                jobId, format: payload.outputFormat, title,
                variationRatio: payload.aspectRatio, progress: 0,
            };
            storeExportJob(videoAdId, exportJob);
            await pollExportJob(exportJob);
        } catch (error) {
            console.error("Export error:", error);
            if (!exportPollingCancelledRef.current) {
                alert(`Export failed: ${error.message || "Unknown error"}`);
            }
        } finally {
            exportInFlightRef.current = false;
            if (!exportPollingCancelledRef.current) {
                setIsExporting(false);
                setExportProgress(0);
            }
        }
    };

    const handleExport = async (settings) => {
        const exportId = activeVariationId || 'original';
        await executeExport(exportId, settings);
    };

    const handleDownloadVariation = async (variationId) => {
        const exportId = variationId === '__original__' ? 'original' : variationId;
        await executeExport(exportId);
    };

    const buildDocumentPayload = () => {
        const items = layers.filter(l => l.id !== 'captions-layer').flatMap(l => l.clips.map(c => ({
            id: l.id,
            type: l.type.toUpperCase(),
            category: l.category || 'main',
            startTime: c.startTime,
            zIndex: l.zIndex || 0,
            volume: l.volume !== undefined ? l.volume : 1, // Include layer volume
            muted: l.muted || false, // Include layer muted state
            clipMuted: c.muted || false,
            visible: c.visible !== false,
            layerVisible: l.visible !== false,
            sharedTransform: l.sharedTransform,
            appearance: c.transform,
            file: c.mediaFile ? { url: c.mediaFile.sourceUrl || c.mediaFile.url, type: c.mediaFile.type, name: c.mediaFile.name } : undefined,
            trim: { start: c.startTime, end: c.startTime + c.duration, duration: c.duration, offset: c.trimStart },
            text: l.type === "text" ? c.text : undefined,
            style: l.type === "text" ? c.style : undefined,
            animation: c.animation,
        })));

        const captionsLayer = layers.find(l => l.id === 'captions-layer');
        const captionsPayload = captionsLayer ? {
            ...dynamicCaptions,
            visible: captionsLayer.visible !== false,
            content: captionsLayer.clips.map(c => ({ startTime: c.startTime, duration: c.duration, text: c.text, words: c.words })),
        } : null;

        const canvas = getDesignCanvas(aspectRatio);
        return { id: videoAdId, canvas: { ...canvas, aspectRatio, orientation: canvas.width === canvas.height ? 'square' : canvas.width > canvas.height ? 'landscape' : 'portrait' }, captions: captionsPayload, timeline: { items } };
    };

    const handleSaveProject = async () => {
        try {
            setIsSaving(true);
            if (activeVariationId) {
                // Save progress for the active variation via PATCH
                const activeVar = variations.find(v => (v.id || v._id) === activeVariationId);
                const editorProgress = {
                    document: buildDocumentPayload(),
                    previewRatio,
                    format: previewRatio,
                    exportRatios,
                    exportCropOffsets,
                };
                // Send complete payload with all existing fields + updated editorProgress
                const payload = {
                    ...activeVar,
                    editorProgress,
                };
                await services.renameVariation(videoAdId, activeVariationId, payload);
                // Update local state so switching away and back preserves changes
                setVariations(prev => prev.map(v => {
                    const vId = v.id || v._id;
                    return vId === activeVariationId ? { ...v, editorProgress } : v;
                }));
            } else {
                // Save original editor progress
                await services.saveEditorProgress(videoAdId, { document: buildDocumentPayload() });
            }
            setHasUnsavedChanges(false);
        } finally { setIsSaving(false); }
    };

    const handleRenameVariation = async (variationId, newTitle) => {
        setVariations(prev => prev.map(v => (v.id || v._id) === variationId ? { ...v, title: newTitle } : v));
        try { await services.renameVariation(videoAdId, variationId, { title: newTitle }); } catch (err) { }
    };

    const handleDeleteVariation = (variationId) => {
        setVariationToDelete(variationId);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!variationToDelete) return;
        const vId = variationToDelete;
        setIsDeleteModalOpen(false);
        setVariationToDelete(null);
        try {
            setDeletingVariationId(vId);
            await services.deleteVariation(videoAdId, vId);
            setVariations(prev => prev.filter(v => (v.id || v._id) !== vId));

            // Remove from export selection if it was selected
            if (selectedForExport.has(vId)) {
                const newSelection = new Set(selectedForExport);
                newSelection.delete(vId);
                setSelectedForExport(newSelection);
            }

            if (activeVariationId === vId) handleSwitchVariation(null);
        } finally { setDeletingVariationId(null); }
    };

    const handleDuplicateVariation = async (variation) => {
        try {
            setIsSavingVariation(true);

            // Determine source data
            let payload;
            const targetId = variation.id || variation._id;

            // Check if we are duplicating the currently active item
            const isActive = (targetId === activeVariationId) || (variation.isOriginal && activeVariationId === null);

            if (isActive) {
                // If duplicating the ACTIVE variation (or active Original), use current editor state
                payload = {
                    document: buildDocumentPayload(),
                    previewRatio: previewRatio,
                    format: previewRatio,
                    exportRatios,
                    exportCropOffsets
                };
            } else if (variation.isOriginal) {
                // Duplicating Original while viewing something else -> Fetch original state
                const response = await services.getEditorTimeline(videoAdId);
                const editorTimeline = response?.data?.editorTimeline || response?.editorTimeline || response?.data;
                const docRoot = editorTimeline?.document || editorTimeline;

                payload = {
                    document: docRoot,
                    previewRatio: "9:16",
                    format: "9:16",
                    exportRatios: ["9:16"],
                    exportCropOffsets: {}
                };
            } else {
                // If duplicating an INACTIVE variation, use its saved state
                // Fallback to defaults if some fields are missing
                const sourceProgress = variation.editorProgress || {};
                const sourceFormat = sourceProgress.format || sourceProgress.previewRatio || "9:16";

                payload = {
                    document: sourceProgress.document, // Creating new variation from saved doc
                    previewRatio: sourceFormat,
                    format: sourceFormat,
                    exportRatios: sourceProgress.exportRatios || [sourceFormat],
                    exportCropOffsets: sourceProgress.exportCropOffsets || {}
                };
            }

            const title = `${variation.title || variation.name} (Copy)`;

            const response = await services.saveVariation(videoAdId, {
                title,
                editorProgress: payload
            });

            const serverVariations = response?.data?.data?.variations || response?.data?.variations || [];
            if (serverVariations.length) setVariations(serverVariations);

        } catch (error) {
            console.error("Failed to duplicate variation:", error);
            // Optionally show error toast
        } finally {
            setIsSavingVariation(false);
        }
    };

    const handleSaveAsNewVariation = async (format) => {
        try {
            setIsSavingVariation(true);
            const ratio = format || previewRatio;
            const title = `${ratio} Reframe`;
            const response = await services.saveVariation(videoAdId, { title, editorProgress: { document: buildDocumentPayload(), format: ratio, previewRatio: ratio, exportRatios: [ratio], exportCropOffsets } });
            const serverVariations = response?.data?.data?.variations || response?.data?.variations || [];

            if (serverVariations.length) {
                setVariations(serverVariations);
                // Auto-select the newly created variation (usually the last one)
                const newVariation = serverVariations[serverVariations.length - 1];
                if (newVariation) {
                    performSwitchVariation(newVariation.id || newVariation._id, newVariation);
                }
            }
            setHasUnsavedChanges(false);
        } finally { setIsSavingVariation(false); }
    };

    const requestSaveAsNewVariation = (format) => {
        if (hasUnsavedChanges) {
            setPendingNewVariationFormat(format);
            setIsUnsavedChangesModalOpen(true);
            return;
        }

        handleSaveAsNewVariation(format);
    };

    const handleSwitchVariation = async (variationId) => {
        if (variationId === activeVariationId) return;

        if (hasUnsavedChanges) {
            setPendingVariationId(variationId);
            setIsUnsavedChangesModalOpen(true);
            return;
        }

        performSwitchVariation(variationId);
    };

    const confirmSwitchVariation = () => {
        setIsUnsavedChangesModalOpen(false);
        setHasUnsavedChanges(false); // Discard changes

        if (blocker.state === "blocked") {
            blocker.proceed();
        } else if (pendingNewVariationFormat !== null) {
            handleSaveAsNewVariation(pendingNewVariationFormat);
            setPendingNewVariationFormat(null);
        } else if (pendingVariationId !== undefined) {
            performSwitchVariation(pendingVariationId);
            setPendingVariationId(undefined);
        }
    };

    const cancelSwitchVariation = () => {
        setIsUnsavedChangesModalOpen(false);
        setPendingVariationId(undefined);
        setPendingNewVariationFormat(null);
        if (blocker.state === "blocked") {
            blocker.reset();
        }
    };

    const performSwitchVariation = async (variationId, variationObject = null) => {
        setActiveVariationId(variationId);
        isInitialLoad.current = true; // Set loading state to prevent dirty tracking during switch

        if (variationId === null) {
            // Switch to Original
            try {
                setIsLoading(true);
                setLoadingProgress(0);
                setLoadingMessage("Fetching original...");
                const response = await services.getEditorTimeline(videoAdId);
                const editorTimeline = response?.data?.editorTimeline || response?.editorTimeline || response?.data;
                const docRoot = editorTimeline?.document || editorTimeline;

                // Reset preview state for Original
                const originalRatio = getDocumentAspectRatio(docRoot);
                setOriginalAspectRatio(originalRatio);
                setPreviewRatio(originalRatio);
                setExportRatios([originalRatio]);
                setExportCropOffsets({});
                setActiveCropFormat(null);

                await reloadLayersFromDocument(docRoot);
            } catch (error) {
                setLoadingMessage("Error loading original.");
                setIsLoading(false);
            }
            return;
        }

        const variation = variationObject || variations.find((v) => (v.id || v._id) === variationId);
        if (!variation) return;

        const docRoot = variation.editorProgress?.document || variation.editorProgress;
        if (!docRoot) return;

        const savedExportRatios = variation.editorProgress?.exportRatios || ["9:16"];
        const savedCropOffsets = variation.editorProgress?.exportCropOffsets || {};
        const savedFormat = variation.editorProgress?.format || variation.editorProgress?.previewRatio || "9:16";

        // Update preview ratio but keep project UI at 9:16
        setPreviewRatio(savedFormat);
        setExportRatios(savedExportRatios);
        setExportCropOffsets(savedCropOffsets);

        // Only clear active crop if the new variation's ratio doesn't match the currently active crop format
        if (activeCropFormat !== savedFormat) {
            setActiveCropFormat(null);
        }

        await reloadLayersFromDocument(docRoot);
    };

    const handleAutoFit = () => {
        if (videoPreviewRef.current) {
            const dims = videoPreviewRef.current.getContentDimensions();
            if (dims) {
                const ratio = `${dims.width}:${dims.height}`;
                setAspectRatio(ratio);
            } else {
                alert("No video content found to match.");
            }
        }
    };

    const selectClip = (clipId) => {
        baseSelectClip(clipId);
        if (clipId) setIsCaptionSelected(false);
    };

    const handleGlobalDeselect = (e) => {
        if (e.target === containerRef.current) {
            selectClip(null);
            setIsCaptionSelected(false);
        }
    };

    const handleCaptionSelect = (selected) => {
        setIsCaptionSelected(selected);
        if (selected) baseSelectClip(null);
    };

    const handleAddTextClip = (layerId) => { setSelectedTextLayerId(layerId); setIsTextModalOpen(true); };
    const handleTextSubmit = (textData) => {
        if (editingClip) {
            const layer = layers.find(l => l.clips.some(c => c.id === editingClip.id));
            if (layer) updateClip(layer.id, editingClip.id, { text: textData.text, style: textData.style });
            setEditingClip(null);
        } else if (selectedTextLayerId) {
            addClipToLayer(selectedTextLayerId, { type: "text", name: "Text", duration: 5 }, currentTime, textData);
        } else {
            const newLayerId = generateId();
            const newClip = { id: generateId(), mediaFile: { type: "text", name: "Text", duration: 5 }, startTime: currentTime, duration: 5, text: textData.text, style: textData.style, transform: { x: 0.5, y: 0.85, scale: 1 } };
            setLayers(prev => [...prev, { id: newLayerId, name: "Text Layer", type: "text", clips: [newClip], visible: true, locked: false, muted: false, category: "text" }]);
        }
    };

    const handleEditText = (clip) => { setEditingClip(clip); setIsTextModalOpen(true); };
    const handleAddMusic = () => setIsMusicModalOpen(true);
    const extractUrl = (data) => {
        if (!data) return null;
        if (typeof data === 'string') return data;
        // Search specific known keys
        if (data.music?.musicUrl) return data.music.musicUrl;
        if (data.data?.music?.musicUrl) return data.data.music.musicUrl;
        if (data.soundEffect?.soundEffectUrl) return data.soundEffect.soundEffectUrl;
        if (data.data?.soundEffect?.soundEffectUrl) return data.data.soundEffect.soundEffectUrl;
        if (data.soundEffect?.audioUrl) return data.soundEffect.audioUrl;
        if (data.data?.soundEffect?.audioUrl) return data.data.soundEffect.audioUrl;

        return data.url || data.audioUrl || data.music?.url || data.soundEffect?.url || data.output?.url || data.data?.url || data.music_url || Object.values(data).find(v => typeof v === 'string' && v.startsWith('http'));
    };

    const extractDuration = (data, defaultDuration) => {
        if (!data) return defaultDuration;
        const durMs = data.music?.durationMs || data.data?.music?.durationMs || data.soundEffect?.durationMs || data.data?.soundEffect?.durationMs;
        if (durMs) return Math.round(durMs / 1000);
        return data.music?.duration || data.soundEffect?.duration || data.duration || defaultDuration;
    };

    const handleMusicGenerated = async (data) => {
        const url = extractUrl(data);
        if (!url) {
            console.error("No valid URL found in generated music data:", data);
            return;
        }

        const newLayerId = generateId();
        const duration = extractDuration(data, 30);
        const name = data?.music?.name || data?.data?.music?.name || "Generated Music";

        const newClip = {
            id: generateId(),
            mediaFile: { url, sourceUrl: url, type: "audio", duration, sourceType: "remote", name },
            startTime: currentTime,
            duration,
            trimStart: 0,
            trimEnd: duration
        };
        setLayers(prev => [...prev, { id: newLayerId, name: "Music", type: "audio", clips: [newClip], visible: true, locked: false, muted: false, category: "music" }]);
        setIsMusicModalOpen(false);
        setHasUnsavedChanges(true);
    };

    const handleSoundEffectGenerated = async (data) => {
        const url = extractUrl(data);
        if (!url) {
            console.error("No valid URL found in generated SFX data:", data);
            return;
        }

        const newLayerId = generateId();
        const duration = extractDuration(data, 5);
        const name = data?.soundEffect?.name || data?.data?.soundEffect?.name || "Sound Effect";

        const newClip = {
            id: generateId(),
            mediaFile: { url, sourceUrl: url, type: "audio", duration, sourceType: "remote", name },
            startTime: currentTime,
            duration,
            trimStart: 0,
            trimEnd: duration
        };
        setLayers(prev => [...prev, { id: newLayerId, name: "SFX", type: "audio", clips: [newClip], visible: true, locked: false, muted: false, category: "soundEffect" }]);
        setIsSoundEffectModalOpen(false);
        setHasUnsavedChanges(true);
    };

    const handleAddFromDrive = (category = 'creativeAssets') => {
        setAssetLibraryCategory(category);
        setIsAssetModalOpen(true);
    };
    const handleAddSoundEffect = () => setIsSoundEffectModalOpen(true);
    const handleRegenerateScene = async () => {
        if (!videoAdId) {
            alert("No Video Ad ID found. Cannot generate a new scene.");
            return;
        }

        let meta = videoAdMeta;

        // Always fetch the canonical video-ad record here. The timeline can contain
        // an original character ID, while the video-ad characterId identifies the
        // exact generated variation used by the scene.
        try {
            setIsLoading(true);
            setLoadingMessage("Loading scene data...");
            const response = await services.getVideoAdById(videoAdId);
            const ad = response?.data?.videoAd || response?.data?.data || response?.data || response;

            if (ad && typeof ad === 'object') {
                const savedCharacter = ad.characterId || ad.character || null;
                const savedCharacterId = savedCharacter?._id || savedCharacter || null;
                const originalCharacterId =
                    ad.originalCharacterId?._id ||
                    ad.originalCharacterId ||
                    savedCharacter?.characterId?._id ||
                    savedCharacter?.characterId ||
                    savedCharacter?.parentCharacterId?._id ||
                    savedCharacter?.parentCharacterId ||
                    savedCharacter?.baseCharacterId?._id ||
                    savedCharacter?.baseCharacterId ||
                    savedCharacterId;
                const fetchedMeta = {
                    adType: ad.adType || ad.type || ad.videoType || null,
                    characterId: savedCharacterId,
                    originalCharacterId,
                    gender:
                        ad.characterId?.attributes?.gender ||
                        ad.character?.attributes?.gender ||
                        ad.characterId?.gender ||
                        ad.character?.gender ||
                        ad.gender ||
                        null,
                    scriptText: ad.scriptText || ad.script || null,
                    voiceId: ad.voiceId || ad.voice?._id || ad.voice?.voiceId || ad.voice || null,
                    brandId: ad.brandId?._id || ad.brandId || ad.brand?._id || ad.brand || null,
                    voiceSettings: ad.voiceSettings || null,
                    audioUrl: getVideoAdAudioUrl(ad),
                    backgroundMedia: ad.backgroundMedia || null,
                };

                // Keep timeline-derived values when the canonical response omits a
                // field, such as audio URLs stored only on older timeline documents.
                meta = Object.keys(fetchedMeta).reduce((merged, key) => ({
                    ...merged,
                    [key]: fetchedMeta[key] || meta?.[key] || null,
                }), {});
                setVideoAdMeta(meta);
            }
        } catch (err) {
            console.error("Failed to fetch video ad meta:", err);
        } finally {
            setIsLoading(false);
        }

        if (!meta || (!meta.characterId && !meta.voiceId && !meta.scriptText)) {
            alert("Could not load scene data. Please try again.");
            return;
        }

        if (!meta.adType) {
            console.warn('[handleRegenerateScene] adType is null — defaulting to speaking_character.');
        }

        const regenerationData = {
            characterId: meta.characterId,
            sceneCharacterId: meta.characterId,
            sceneVariationId: meta.characterId,
            originalCharacterId: meta.originalCharacterId || meta.characterId,
            gender: meta.gender,
            scriptText: meta.scriptText,
            voiceId: meta.voiceId,
            brandId: meta.brandId,
            voiceSettings: meta.voiceSettings,
            audioUrl: meta.audioUrl,
            backgroundMedia: meta.backgroundMedia,
        };


        if (meta.adType === 'talking_head') {
            navigate('/app/talking-head', {
                state: {
                    regenerationData,
                    returnUrl: '/app/videoeditor',
                    videoAdId,
                }
            });
        } else {
            // Default: speaking_character
            navigate('/app/speaking-character', {
                state: {
                    regenerationData,
                    returnUrl: '/app/videoeditor',
                    videoAdId,
                }
            });
        }
    };

    const handleAssetSelect = (assets) => {
        assets.forEach(asset => {
            const isAudio = asset.type === 'audio' || asset.mimeType?.startsWith('audio/');
            const type = isAudio ? 'audio' : (asset.type === 'video' ? 'video' : 'image');
            const newLayerId = generateId();

            let clipDuration = 10;
            if (isAudio && asset.duration) clipDuration = asset.duration;
            else if (asset.duration) clipDuration = asset.duration;

            const newClip = { 
                id: generateId(), 
                mediaFile: { ...asset, sourceType: 'remote' }, 
                startTime: currentTime, 
                duration: clipDuration, 
                transform: { x: 0.5, y: 0.5, scale: 1 } 
            };

            if (isAudio) {
                newClip.trimStart = 0;
                newClip.trimEnd = clipDuration;
            }

            const category = isAudio ? (asset.folderType === 'soundEffect' ? 'soundEffect' : 'music') : 'main';

            setLayers(prev => [...prev, { 
                id: newLayerId, 
                name: asset.name, 
                type, 
                clips: [newClip], 
                visible: true, 
                locked: false,
                muted: false,
                category 
            }]);
        });
        setIsAssetModalOpen(false);
        setIsMusicModalOpen(false);
        setIsSoundEffectModalOpen(false);
        setHasUnsavedChanges(true); // Direct mutation
    };

    const handleDividerPointerDown = (e) => { e.preventDefault(); setIsDraggingDivider(true); };

    React.useEffect(() => {
        const move = (e) => {
            if (!isDraggingDivider) return;
            const rect = containerRef.current.getBoundingClientRect();
            setBottomSectionHeight(Math.max(20, Math.min(80, ((rect.bottom - e.clientY) / rect.height) * 100)));
        };
        const up = () => setIsDraggingDivider(false);
        window.addEventListener("pointermove", move);
        window.addEventListener("pointerup", up);
        window.addEventListener("pointercancel", up);
        return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up); };
    }, [isDraggingDivider]);

    const touchDevice = React.useMemo(() => window.matchMedia?.("(any-pointer: coarse)")?.matches ?? false, []);

    return (
        <DndProvider backend={touchDevice ? TouchBackend : HTML5Backend} options={touchDevice ? { enableMouseEvents: true, delayTouchStart: 150 } : undefined}>
            <div className="video-editor-workbench h-full flex flex-col bg-[#FAFAFA] dark:bg-[#111111] text-gray-900 dark:text-white overflow-hidden">
                <Toolbar onBack={handleBack} onExport={handleExport} onAddLayer={wrappedAddLayer} aspectRatio={aspectRatio} onAspectRatioChange={setAspectRatio} selectedClipId={selectedClipId} layers={layers} onUpdateClip={wrappedUpdateClip} onAutoFit={handleAutoFit} exportRatios={exportRatios} onToggleExportRatio={toggleExportRatio} onSaveProject={handleSaveProject} isSaving={isSaving} isExporting={isExporting} selectedExportCount={selectedForExport.size} hasVariations={variations.length > 0} />
                <div className="video-editor-panel-controls" aria-label="Editor panels">
                    <button type="button" aria-expanded={isLeftPanelOpen} className={isLeftPanelOpen ? 'is-active' : ''} onClick={() => { setIsLeftPanelOpen(true); setIsRightPanelOpen(false); }}>Tools</button>
                    <button type="button" aria-expanded={isRightPanelOpen} className={isRightPanelOpen ? 'is-active' : ''} onClick={() => { setIsRightPanelOpen(true); setIsLeftPanelOpen(false); }}>Properties</button>
                </div>
                <div ref={containerRef} onClick={handleGlobalDeselect} className="flex-1 flex flex-col overflow-hidden relative editor-main-content">
                    <div style={{ height: isTimelineCollapsed ? 'calc(100% - 110px)' : `${100 - bottomSectionHeight}%`, transition: isDraggingDivider ? 'none' : 'height 400ms cubic-bezier(0.4, 0, 0.2, 1)' }} className="flex w-full min-h-0 relative z-0">
                        <ResponsivePanel open={isLeftPanelOpen} onClose={() => setIsLeftPanelOpen(false)} side="left" label="Editor tools">
                          <div className="h-full border-r border-[#E5E7EB] dark:border-white/10 z-10 shrink-0">
                            <LeftSidebar onAddMedia={onAddMedia} onAddMusic={handleAddMusic} onAddText={handleGlobalAddText} onRegenerateCaptions={handleRegenerateCaptions} onAddSoundEffect={handleAddSoundEffect} onRegenerateScene={handleRegenerateScene} />
                          </div>
                        </ResponsivePanel>
                        <div className="flex-1 bg-black relative overflow-hidden h-full preview-container">
                            <VideoPreview ref={videoPreviewRef} layers={layers} dynamicCaptions={dynamicCaptions} currentTime={currentTime} isPlaying={isPlaying} aspectRatio={aspectRatio} selectedClipId={selectedClipId} onSelectClip={selectClip} onUpdateClip={wrappedUpdateClip} onUpdateLayerTransform={wrappedUpdateLayerTransform} onPlay={play} onPause={pause} onStop={stop} onSeek={seek} overlayRatio={previewRatio} exportCropOffsets={exportCropOffsets} activeCropFormat={activeCropFormat} onUpdateCropOffset={handleUpdateExportCropOffsets} onUpdateDynamicCaptions={handleUpdateDynamicCaptions} onOpenCaptionSettings={() => setIsCaptionsModalOpen(true)} isCaptionSelected={isCaptionSelected} onSelectCaption={handleCaptionSelect} isLoading={isLoading} loadingProgress={loadingProgress} loadingMessage={loadingMessage} onEditText={handleEditText} isExportingMode={isExporting} exportProgress={exportProgress} exportMessage={loadingMessage} />
                        </div>
                        <ResponsivePanel open={isRightPanelOpen} onClose={() => setIsRightPanelOpen(false)} side="right" label="Editor properties">
                          <div className="h-full border-l border-[#E5E7EB] dark:border-white/10 bg-white dark:bg-[#111111]">
                            <RightSidebar selectedClip={selectedClip} selectedLayerId={selectedLayerId} onUpdateClip={wrappedUpdateClip} exportRatios={exportRatios} aspectRatios={exportRatios} onToggleExportRatio={toggleExportRatio} previewAspectRatio={previewRatio} originalAspectRatio={originalAspectRatio} onPreviewAspectRatioChange={setPreviewRatio} activeCropFormat={activeCropFormat} onToggleCropMode={setActiveCropFormat} variations={variations} activeVariationId={activeVariationId} onSwitchVariation={handleSwitchVariation} onSaveAsNew={requestSaveAsNewVariation} onRenameVariation={handleRenameVariation} onDeleteVariation={handleDeleteVariation} onDownloadVariation={handleDownloadVariation} onDuplicateVariation={handleDuplicateVariation} isSavingVariation={isSavingVariation} deletingVariationId={deletingVariationId} selectedForExport={selectedForExport} onToggleExportSelection={handleToggleExportSelection} onExport={handleExport} />
                          </div>
                        </ResponsivePanel>
                    </div>
                    <div onPointerDown={!isTimelineCollapsed ? handleDividerPointerDown : undefined} className={`video-editor-divider absolute left-0 right-0 z-20 h-4 -mt-2 flex items-center justify-center group ${isTimelineCollapsed ? 'pointer-events-none' : 'cursor-ns-resize'}`} style={{ top: isTimelineCollapsed ? 'calc(100% - 110px)' : `${100 - bottomSectionHeight}%`, transition: isDraggingDivider ? 'none' : 'top 400ms cubic-bezier(0.4, 0, 0.2, 1)' }}>
                        <div className="w-full h-0.5 bg-gray-300 dark:bg-gray-700 group-hover:bg-gray-400 dark:group-hover:bg-orange-300 transition-colors"></div>
                    </div>
                    <div style={{ height: isTimelineCollapsed ? '110px' : `${bottomSectionHeight}%`, transition: isDraggingDivider ? 'none' : 'height 400ms cubic-bezier(0.4, 0, 0.2, 1)' }} className="flex flex-col min-h-0 z-10 bg-white dark:bg-[#111111] relative w-full timeline-container">

                        <div className="video-editor-timeline-toolbar bg-white dark:bg-[#111111] border-[#E5E7EB] dark:border-white/10 px-6 py-3 shadow-sm z-30 relative">
                            <div className="video-editor-timeline-toolbar-inner flex items-center justify-between max-w-[100%] mx-auto">
                                <div className="flex items-center">
                                    <button onClick={handleToggleTimeline} className="p-1.5 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition-all text-gray-600 dark:text-gray-300 hover:text-orange-500"><ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isTimelineCollapsed ? 'rotate-180' : ''}`} /></button>
                                    <div className="flex items-center gap-1 ml-4 border-l border-gray-200 dark:border-white/10 pl-4"><button onClick={handleZoomOut} className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors hover:bg-gray-100 dark:hover:bg-white/5 rounded"><ZoomOut className="w-4 h-4" /></button><button onClick={handleZoomIn} className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors hover:bg-gray-100 dark:hover:bg-white/5 rounded"><ZoomIn className="w-4 h-4" /></button><button onClick={handleFitToScreen} className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors ml-1 hover:bg-gray-100 dark:hover:bg-white/5 rounded"><Minimize2 className="w-3.5 h-3.5" /><span>Fit</span></button></div>
                                </div>
                                <div className="video-editor-playback-controls absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-3"><button onClick={() => seek(0)} className="flex items-center justify-center h-8 w-8 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/5 transition-colors"><SkipBack className="h-4 w-4" /></button>
                                    {isGeneratingCaptions || isLoading ? <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-orange-500"></div> : <button onClick={isPlaying ? pause : play} className="flex items-center justify-center h-9 w-9 rounded transition-all hover:scale-105 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/5">{isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}</button>}
                                    <button onClick={() => seek(duration)} className="flex items-center justify-center h-8 w-8 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/5 transition-colors"><SkipForward className="h-4 w-4" /></button>
                                </div>
                                <div className="video-editor-timecode ml-auto text-sm font-mono leading-tight text-gray-900 dark:text-white">{formatTime(currentTime)} / <span className="text-gray-400 dark:text-gray-600">{formatTime(duration)}</span></div>
                            </div>
                        </div>
                        {!isTimelineCollapsed && (
                            <div className="flex-1 min-h-0 overflow-hidden" ref={timelineContainerRef}>
                                <Timeline layers={layers} currentTime={currentTime} duration={duration} zoom={zoom} onSeek={seek} onToggleVisibility={wrappedToggleLayerVisibility} onToggleLock={wrappedToggleLayerLock} onToggleMute={wrappedToggleLayerMute} onRemoveLayer={wrappedRemoveLayer} onAddClip={wrappedAddClipToLayer} onRemoveClip={wrappedRemoveClip} onSplitClip={wrappedSplitClip} onUpdateClip={wrappedUpdateClip} onAddTextClip={handleAddTextClip} onToggleSharedTransform={wrappedToggleSharedTransform} onReorderLayers={wrappedReorderLayers} onSelectClip={baseSelectClip} onUpdateVolume={wrappedUpdateLayerVolume} disabled={isLoading} />
                            </div>
                        )}
                    </div>
                </div>
                <TextInputModal isOpen={isTextModalOpen} initialData={editingClip} onClose={() => { setIsTextModalOpen(false); setEditingClip(null); }} onSubmit={handleTextSubmit} />
                <MusicLibraryModal isOpen={isMusicModalOpen} onClose={() => setIsMusicModalOpen(false)} videoAdId={videoAdId} onGenerate={handleMusicGenerated} onSelectTrack={(track) => handleMusicGenerated({ music: track })} onAddFromDrive={() => handleAddFromDrive('music')} videoDuration={duration} />
                <SoundEffectModal isOpen={isSoundEffectModalOpen} onClose={() => setIsSoundEffectModalOpen(false)} videoAdId={videoAdId} onGenerate={handleSoundEffectGenerated} onSelectTrack={(track) => handleSoundEffectGenerated({ soundEffect: track })} onAddFromDrive={() => handleAddFromDrive('soundEffect')} />
                <AdAssetLibraryModal isOpen={isAssetModalOpen} onClose={() => setIsAssetModalOpen(false)} onSelect={handleAssetSelect} initialCategory={assetLibraryCategory} />
                <CaptionsCustomizationModal isOpen={isCaptionsModalOpen} onClose={() => setIsCaptionsModalOpen(false)} appearance={dynamicCaptions?.appearance} styles={dynamicCaptions?.styles || dynamicCaptions?.style} content={dynamicCaptions?.content} currentTime={currentTime} onUpdate={(updated) => { setDynamicCaptions(prev => ({ ...prev, appearance: updated.appearance, styles: updated.styles, content: updated.content || prev.content, settings: updated.settings || prev.settings })); if (updated.content) setLayers(prev => prev.map(layer => (layer.id === 'captions-layer') ? { ...layer, clips: updated.content.map((segment, idx) => ({ id: `caption-clip-${idx}`, startTime: segment.startTime, duration: segment.duration, text: segment.text || segment.words.map(w => w.content).join(' '), words: segment.words })) } : layer)); setHasUnsavedChanges(true); }} onPreviewUpdate={(appearance) => setDynamicCaptions(prev => ({ ...prev, appearance }))} />
                <ConfirmModal isOpen={isDeleteModalOpen} onClose={() => { setIsDeleteModalOpen(false); setVariationToDelete(null); }} title="Delete Variation" message="Are you sure you want to delete this variation? This action cannot be undone." confirmLabel="Delete" cancelLabel="Cancel" onConfirm={handleConfirmDelete} />
                <ConfirmModal isOpen={isUnsavedChangesModalOpen} onClose={cancelSwitchVariation} title="Unsaved Changes" message="You have unsaved changes. Are you sure you want to leave? Your changes will be lost." confirmLabel="Discard Changes" cancelLabel="Cancel" onConfirm={confirmSwitchVariation} />
                <input type="file" ref={audioInputRef} onChange={(e) => processUploadedFile(e, "audio")} style={{ display: "none" }} />
            </div>
        </DndProvider>
    );
}

function VideoEditorPhoneNotice() {
    const location = useLocation();
    const navigate = useNavigate();

    const goBack = () => {
        if (location.state?.returnUrl) {
            navigate(location.state.returnUrl, { state: { ...location.state } });
        } else {
            navigate(-1);
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center overflow-y-auto bg-slate-950/60 px-5 py-8 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="video-editor-phone-title" aria-describedby="video-editor-phone-description">
            <div className="my-auto max-h-full w-full max-w-sm overflow-y-auto rounded-3xl border border-gray-200 bg-white p-6 text-center shadow-2xl dark:border-white/10 dark:bg-[#171717] sm:p-8">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-400">
                    <MonitorPlay size={32} strokeWidth={1.8} aria-hidden="true" />
                </div>
                <h1 id="video-editor-phone-title" className="text-xl font-bold tracking-tight text-gray-950 dark:text-white">Edit your video on a larger screen</h1>
                <p id="video-editor-phone-description" className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
                    The video editor works best on a tablet, laptop, or desktop, where you have room to arrange clips and fine-tune your video. Open Zimzee on one of those devices to continue editing.
                </p>
                <button type="button" onClick={goBack} autoFocus className="mt-7 w-full rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500">
                    Go back
                </button>
            </div>
        </div>
    );
}

function VideoEditorPage() {
    const [isPhone, setIsPhone] = React.useState(() => window.matchMedia?.("(max-width: 767px)")?.matches ?? false);

    React.useEffect(() => {
        const query = window.matchMedia?.("(max-width: 767px)");
        if (!query) return undefined;
        const update = () => setIsPhone(query.matches);
        if (query.addEventListener) {
            query.addEventListener("change", update);
            return () => query.removeEventListener("change", update);
        }
        query.addListener(update);
        return () => query.removeListener(update);
    }, []);

    return isPhone ? <VideoEditorPhoneNotice /> : <VideoEditorWorkspace />;
}

export default VideoEditorPage;
